import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { ArrowRight, Plus, Trash2 } from "lucide-react";

import { SPECIES, type SpeciesKey } from "@shared/schema";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parseGeneList } from "@/lib/utils";

const SPECIES_ORDER: SpeciesKey[] = [
  "human",
  "mouse",
  "rat",
  "zebrafish",
  "drosophila",
  "c_elegans",
  "yeast",
  "arabidopsis",
];

export function SavedPanelsPage() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["panels"], queryFn: () => api.listPanels() });

  const create = useMutation({
    mutationFn: api.createPanel,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["panels"] }),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.deletePanel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["panels"] }),
  });

  const [name, setName] = useState("");
  const [genes, setGenes] = useState("");
  const [species, setSpecies] = useState<SpeciesKey>("human");
  const [notes, setNotes] = useState("");

  const onCreate = (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseGeneList(genes);
    if (!name.trim() || parsed.length === 0) return;
    create.mutate(
      { name: name.trim(), genes: parsed, species, notes: notes.trim() || undefined },
      {
        onSuccess: () => {
          setName("");
          setGenes("");
          setNotes("");
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold">Saved panels</h1>
        <p className="text-sm text-muted-foreground">
          Persist gene sets for reuse. v1 will evolve these into project-scoped ResultSnapshots —
          the schema already reserves metadata fields for that.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4" /> Create panel
          </CardTitle>
          <CardDescription>Stored locally in SQLite.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid grid-cols-1 gap-3 md:grid-cols-2" onSubmit={onCreate}>
            <div className="space-y-1">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.currentTarget.value)} placeholder="DMD fibrosis pilot" />
            </div>
            <div className="space-y-1">
              <Label>Species</Label>
              <select
                value={species}
                onChange={(e) => setSpecies(e.currentTarget.value as SpeciesKey)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              >
                {SPECIES_ORDER.map((k) => (
                  <option key={k} value={k}>
                    {SPECIES[k].common}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Genes</Label>
              <Textarea
                rows={2}
                value={genes}
                onChange={(e) => setGenes(e.currentTarget.value)}
                placeholder="DMD, DAG1, SGCA"
                className="font-mono text-sm"
              />
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Notes (optional)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
            </div>
            <div className="md:col-span-2 flex items-center justify-end">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Saving…" : "Save panel"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h2 className="font-display text-lg font-semibold">Your panels</h2>
        {list.isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
        {list.data && list.data.length === 0 && (
          <p className="text-sm text-muted-foreground">No panels yet — save one above.</p>
        )}
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {list.data?.map((p) => {
            const parsedGenes = tryParse(p.genes);
            return (
              <li key={p.id}>
                <Card>
                  <CardHeader className="flex-row items-start justify-between gap-2">
                    <div>
                      <CardTitle>{p.name}</CardTitle>
                      <CardDescription>
                        {SPECIES[p.species as SpeciesKey]?.common ?? p.species} · {parsedGenes.length} genes
                      </CardDescription>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => remove.mutate(p.id)}
                      aria-label="Delete panel"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="truncate font-mono text-xs text-muted-foreground">
                      {parsedGenes.join(", ")}
                    </p>
                    {p.notes && <p className="text-xs text-muted-foreground">{p.notes}</p>}
                    <Link
                      to={`/results?genes=${encodeURIComponent(parsedGenes.join(","))}&species=${p.species}`}
                      className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                    >
                      Run analysis <ArrowRight className="h-3 w-3" />
                    </Link>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

function tryParse(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
}
